import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Icon from '../Icon';
import ConnectorDetailCell from './ConnectorDetailCell';
import SessionLabel from './SessionLabel';
import LoadingAnimation from '../Utils/BateryChargeAnimation';
import Button from '../Utils/Button';
import IfRender from '../Utils/IfRender';

import { useStartStopTransactionMutation } from '../../redux/api/transactions';
import { setIsButtonLoading } from '../../redux/reducers/chargingPointSlice';
import { setIsButtonLoadingForId } from '../../redux/reducers/chargingPointsSlice';
import { selectAuthData } from '../../redux/selectors/auth';
import { selectIsButtonLoading } from '../../redux/selectors/chargingPoint';
import { selectIsButtonLoadingForId } from '../../redux/selectors/chargingPoints';
import { userRolesOnOrganizationFilter } from '../../QueryFilters/queries';
import {
  getConnectorDetails,
  getControlButtonProps,
  getStatusClassName
} from '../../Utils/connectors';
import { CHARGING_STATES } from '../../Constants';
import { useLocation } from 'react-router';
import { ROLES } from '../../Constants';
import './style.scss';
import { selectUserType } from '../../redux/selectors/userType.js';
import { useLazyCheckUserOrganizationAccessQuery } from '../../redux/api/organizations';
const sessionLabelStates = [
  CHARGING_STATES.CHARGING,
  CHARGING_STATES.PREPARING,
  CHARGING_STATES.FINISHING
];

export default function Connector({
  connector = {},
  children = null,
  hideButton = false,
  fromCharge = false,
  isSession = false
}) {
  const status = connector.status || null;
  const hasPrice =
    connector?.chargingPoint?.prices?.length > 0
      ? +connector.chargingPoint?.prices[connector?.chargingPoint?.prices?.length - 1]?.price > 0
      : false;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user: { id: authorizedUserId = '' } = {}, organizationId } = useSelector(selectAuthData);
  const { isAdmin, isSuperAdmin, isProviderAdmin } = useSelector(selectUserType);
  const [checkAccess] = useLazyCheckUserOrganizationAccessQuery();

  const isButtonLoading = useSelector(selectIsButtonLoading);
  const isButtonLoadingForId = useSelector((state) =>
    selectIsButtonLoadingForId(state, connector.chargePointId, connector.id)
  );
  const isLoading = fromCharge ? isButtonLoading : isButtonLoadingForId;

  const [logo, setLogo] = useState('connector__company_container');
  const [isLabelActive, setIsLabelActive] = useState(false);

  const [startStopTransaction, { data }] = useStartStopTransactionMutation();
  const route = useLocation();

  useEffect(() => {
    if (!route.pathname.includes('dashboard')) {
      setLogo('connector__company_container--session');
    }
  }, [route]);
  const isAdminOrSuperAdmin =
    (organizationId === connector.organization_id && isAdmin) || isSuperAdmin;

  const hasStopAccess = !!authorizedUserId
    ? connector?.user_id === authorizedUserId || isAdminOrSuperAdmin
    : false;

  const logoSrc = useMemo(() => {
    if (
      connector.chargingPoint.organization &&
      connector.chargingPoint.organization.organizationProperties
    ) {
      const organizationProperties = connector.chargingPoint.organization.organizationProperties;
      return organizationProperties.find((prop) => prop.propertyName === 'provider_logo')
        ?.propertyValue;
    }

    return '';
  }, [connector.chargingPoint?.organization?.organizationProperties]);

  const setLoadingButton = () => {
    fromCharge
      ? dispatch(setIsButtonLoading({ isLoading: true }))
      : dispatch(
          setIsButtonLoadingForId({
            isLoading: true,
            chargePointId: connector.chargePointId,
            chargePointConnectorId: connector.id
          })
        );
  };

  const startStopCharging = async () => {
    console.log('startStopCharging', status);
    if (status === CHARGING_STATES.CHARGING) {
      setLoadingButton();
      let transactionId;
      if (connector.transactionId) {
        transactionId = connector.transactionId;
      } else if (connector.sessions && connector.sessions.length > 0) {
        transactionId = connector.sessions[connector.sessions.length - 1].id;
      } else {
        return;
      }

      try {
        await startStopTransaction({
          transactionName: 'RemoteStopTransaction',
          params: {
            chargePointId: connector.chargePointId,
            chargePointConnectorId: connector.id,
            transactionId,
            optional: {
              idTag: 'web',
              transactionId
            }
          }
        });
      } catch (error) {
        fromCharge
          ? dispatch(setIsButtonLoading({ isLoading: false }))
          : dispatch(
              setIsButtonLoadingForId({
                isLoading: false,
                chargePointId: connector.chargePointId,
                chargePointConnectorId: connector.id
              })
            );
      }
    } else {
      setLoadingButton();

      const { data: userRolesOnOrganization } = await checkAccess({
        organizationId: connector.chargingPoint?.organization?.id,
        filter: JSON.stringify(userRolesOnOrganizationFilter)
      });
      const hasAccess = userRolesOnOrganization.some(
        (role) => role.role.name === ROLES.ADMIN || role.role.name === ROLES.SUPER_ADMIN
      );

      if ((hasPrice && !hasAccess) || !isSuperAdmin) {
        navigate(`/charge/${connector.chargePointId}/${connector.id}`);
      } else {
        setLoadingButton();

        try {
          await startStopTransaction({
            transactionName: 'RemoteStartTransaction',
            params: {
              chargePointId: connector.chargePointId,
              chargePointConnectorId: connector.id,
              label: connector.label,
              transactionId: connector.transactionId,
              optional: {
                idTag: 'web',
                transactionId: connector.transactionId
              }
            }
          });
        } catch (error) {
          fromCharge
            ? dispatch(setIsButtonLoading({ isLoading: false }))
            : dispatch(
                setIsButtonLoadingForId({
                  isLoading: false,
                  chargePointId: connector.chargePointId,
                  chargePointConnectorId: connector.id
                })
              );
        }
      }
    }
  };
  //RENDER COMPONENT
  //+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  return (
    <div {...getStatusClassName({ status, className: 'connector' })}>
      <div className='connector__header'>
        {/* {connector?.chargingPoint?.client_logo && */}
        <div className={logo}>
          <p>Powered by:</p>
          <img src={logoSrc} alt='' />
        </div>
        {/* } */}
        <h2 className='connector__name'>
          {connector?.chargingPoint?.name || 'N/A'}
          <IfRender condition={connector.remoteId}>
            <span className='connector__number'> - Connector {connector.remoteId}</span>
          </IfRender>
        </h2>
        <IfRender condition={status}>
          <Icon iconName={status} label={t(status?.toLowerCase())} />
        </IfRender>
      </div>
      <div className='connector__details'>
        <IfRender condition={status === CHARGING_STATES.CONNECTING}>
          <LoadingAnimation />
        </IfRender>
        {getConnectorDetails(connector).map((cellProps, index) => (
          <ConnectorDetailCell key={`${connector.id}-${connector.id}-${index}`} {...cellProps} />
        ))}
      </div>
      <IfRender condition={isSuperAdmin || isProviderAdmin}>
        <a
          className={'connector__link'}
          href={`/status-notifications/${connector.chargingPoint.id}`}>
          {t('view_charger_notifications')}
        </a>
      </IfRender>
      <IfRender condition={connector?.user?.userName}>
        <p className={'connector__charge_username'}>{`${t('currently_charging')}  ${
          connector?.user?.userName
        }`}</p>
      </IfRender>
      <IfRender condition={status !== CHARGING_STATES.OFFLINE}>
        <div className='connector__sepatator' />
        <div className='connector__controls'>
          <IfRender condition={status === CHARGING_STATES.AVAILABLE}>
            <p className='connector__message'>{t('please_connect_power_plug')}</p>
          </IfRender>
          <IfRender
            condition={
              sessionLabelStates.includes(status) &&
              route.pathname.includes('dashboard') &&
              connector?.sessions?.length > 0
            }>
            <SessionLabel
              isActive={isLabelActive}
              setIsActive={setIsLabelActive}
              label={
                (connector?.sessions &&
                  connector?.sessions[connector?.sessions?.length - 1]?.label) ||
                ''
              }
              sessionId={
                connector?.sessions && connector?.sessions[connector?.sessions?.length - 1]?.id
              }
              transactionId={connector?.transactionId}
            />
          </IfRender>
          <IfRender condition={!isLabelActive}>
            <Button
              hide={hideButton}
              {...getControlButtonProps({ status, isLoading, hasStopAccess, isSession })}
              onClick={startStopCharging}
            />
          </IfRender>
        </div>

        <IfRender condition={children}>
          <div className='connector__sepatator' />
          {children}
        </IfRender>
      </IfRender>
    </div>
  );
}

Connector.propTypes = {
  connector: PropTypes.object,
  children: PropTypes.node,
  hideButton: PropTypes.bool
};
